import { Component } from '@theme/component';
import { canPrefetch, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} ModalConfig
 * @property {string} [src]
 * @property {string} [handle]
 * @property {number|string} [variantId]
 * @property {boolean} [isFree]
 * @property {boolean} [hasDiscount]
 * @property {number} [originalPriceCents]
 * @property {number} [finalPriceCents]
 * @property {string} [originalPriceMoney]
 * @property {string} [finalPriceMoney]
 * @property {string} [badgeText]
 * @property {string} [yotpoAppKey]
 * @property {string} [featuresMore]
 * @property {string} [featuresLess]
 * @property {string} [titleId]
 */

/**
 * @typedef {Object} ModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [body]
 * @property {HTMLElement} [loader]
 * @property {HTMLScriptElement} [config]
 * @property {Element} [slider]
 * @property {Element[]} [slides]
 * @property {Element[]} [dots]
 * @property {HTMLTemplateElement} [deferredSlides]
 * @property {HTMLTemplateElement} [deferredDots]
 */

/** @type {Map<string, string>} */
const modalHtmlCache = new Map();

/** @type {Map<string, Promise<string>>} */
const modalHtmlPending = new Map();

/**
 * @param {string} src
 * @param {AbortSignal} [signal]
 * @returns {Promise<string>}
 */
function getModalHtml(src, signal) {
    const cached = modalHtmlCache.get(src);
    if (cached) return Promise.resolve(cached);

    const pending = modalHtmlPending.get(src);
    if (pending) return pending;

    const request = fetch(src, signal ? { signal } : undefined)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Failed to load product modal (${response.status})`);
            }
            return response.text();
        })
        .then((html) => {
            modalHtmlCache.set(src, html);
            modalHtmlPending.delete(src);
            warmHeroImage(html);
            return html;
        })
        .catch((error) => {
            modalHtmlPending.delete(src);
            throw error;
        });

    modalHtmlPending.set(src, request);
    return request;
}

/**
 * @param {string} html
 */
function warmHeroImage(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const image = parsed.querySelector('img.room-info__modal-image');
    if (!(image instanceof HTMLImageElement)) return;
    const src = image.currentSrc || image.src;
    if (!src) return;
    const preload = new Image();
    preload.src = src;
}

/**
 * Room Product Modal Component
 *
 * Wraps a native <dialog> and provides a scroll-snap image slider for the
 * product info modal. Shared between the Room Info section and the AD Room
 * Banner section. Registration is guarded so multiple definitions across
 * sections/branches are safe.
 *
 * Storefront Home keeps an empty dialog shell; the body is fetched once via
 * the Section Rendering API and cached by product URL.
 *
 * @extends {Component<ModalRefs>}
 */
export class RoomBannerModalComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['dialog'];

    /** @type {AbortController|null} */
    #abortController = null;

    /** @type {AbortController|null} */
    #fetchController = null;

    /** @type {AbortController|null} */
    #sliderAbort = null;

    /** @type {HTMLElement|null} */
    #trigger = null;

    /** @type {number} */
    #currentSlide = 0;

    /** @type {number} */
    #scrollTimer = 0;

    /** @type {boolean} */
    #ignoreClick = false;

    /** @type {boolean} */
    #ready = false;

    /** @type {boolean} */
    #sliderBound = false;

    connectedCallback() {
        super.connectedCallback();
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;

        this.refs.dialog.addEventListener('click', this.#handleDialogClick, { signal });
        this.refs.dialog.addEventListener('cancel', this.#handleCancel, { signal });
        this.refs.dialog.addEventListener('keydown', this.#handleKeydown, { signal });

        if (this.querySelector('.room-info__modal-wrapper')) {
            this.#ready = true;
            this.#bindSlider();
        }
    }

    updatedCallback() {
        super.updatedCallback();
        this.#currentSlide = 0;
        this.#ready = Boolean(this.querySelector('.room-info__modal-wrapper'));
        this.#sliderBound = false;
        if (this.#ready) this.#bindSlider();
        const dotsTrack = this.querySelector('.room-info__modal-dots-track');
        if (dotsTrack instanceof HTMLElement) dotsTrack.style.transform = '';
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
        this.#fetchController?.abort();
        this.#sliderAbort?.abort();
        clearTimeout(this.#scrollTimer);
    }

    prefetch() {
        if (this.#ready || !canPrefetch()) return;
        const src = this.#sourceUrl();
        if (!src) return;
        getModalHtml(src).catch((error) => {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error(error);
        });
    }

    /**
     * Moves slides and dots after the first one out of their <template> into the DOM.
     * Only the first slide is rendered up front so closed modals stay cheap.
     */
    hydrate() {
        const { deferredSlides, deferredDots } = this.refs;
        let hydrated = false;

        for (const template of [deferredSlides, deferredDots]) {
            if (!(template instanceof HTMLTemplateElement)) continue;
            template.replaceWith(template.content);
            hydrated = true;
        }

        if (hydrated) this.updatedCallback();
    }

    async openModal() {
        const { dialog } = this.refs;
        if (dialog.open) return;

        const active = document.activeElement;
        this.#trigger = active instanceof HTMLElement ? active : null;

        this.#currentSlide = 0;
        if (!this.#ready) this.#setBusy(true);
        dialog.showModal();

        if (!this.#ready) {
            await this.#loadBody();
        }

        if (!dialog.open) return;
        this.hydrate();
        this.#resetSlider();
    }

    closeModal() {
        this.#fetchController?.abort();
        this.#setBusy(false);

        const { dialog } = this.refs;
        if (dialog.open) dialog.close();
        this.#restoreFocus();
    }

    prevSlide() {
        const slides = this.refs.slides;
        if (!slides?.length || slides.length <= 1) return;
        this.#goToSlideIndex((this.#currentSlide - 1 + slides.length) % slides.length);
    }

    nextSlide() {
        const slides = this.refs.slides;
        if (!slides?.length || slides.length <= 1) return;
        this.#goToSlideIndex((this.#currentSlide + 1) % slides.length);
    }

    /** @param {Event} event */
    goToSlide(event) {
        const index = parseInt(/** @type {HTMLElement} */ (event.target).dataset.index ?? '', 10);
        if (!Number.isNaN(index)) this.#goToSlideIndex(index);
    }

    toggleFeatures() {
        const props = this.querySelector('.room-info__modal-props');
        if (!props) return;
        const isCollapsed = props.classList.contains('room-info__modal-props--collapsed');
        props.classList.toggle('room-info__modal-props--collapsed');
        const btn = this.querySelector('.room-info__features-toggle');
        if (btn) {
            btn.setAttribute('aria-expanded', String(isCollapsed));
        }
    }

    /** @returns {ModalConfig} */
    #readConfig() {
        const script = this.refs.config;
        if (script instanceof HTMLScriptElement && script.textContent) {
            try {
                return /** @type {ModalConfig} */ (JSON.parse(script.textContent));
            } catch (error) {
                console.error(error);
            }
        }

        return {
            src: this.dataset.modalSrc,
            isFree: this.dataset.isFree === 'true',
            yotpoAppKey: this.dataset.yotpoAppKey,
        };
    }

    /** @returns {string} */
    #sourceUrl() {
        return this.#readConfig().src || this.dataset.modalSrc || '';
    }

    async #loadBody() {
        const src = this.#sourceUrl();
        if (!src) {
            this.closeModal();
            return;
        }

        this.#fetchController?.abort();
        this.#fetchController = new AbortController();
        const { signal } = this.#fetchController;
        const config = this.#readConfig();

        this.#setBusy(true);

        try {
            const html = await getModalHtml(src, signal);
            if (signal.aborted || !this.refs.dialog.open) return;
            await this.#inject(html, config, signal);
        } catch (error) {
            if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
            this.closeModal();
            console.error(error);
        }
    }

    /**
     * @param {string} html
     * @param {ModalConfig} config
     * @param {AbortSignal} signal
     */
    async #inject(html, config, signal) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = parsed.querySelector('.room-info__modal-wrapper');
        if (!(wrapper instanceof HTMLElement)) {
            throw new Error('Product modal markup missing');
        }

        this.#applyBannerSettings(wrapper, config);

        const { body } = this.refs;
        if (!(body instanceof HTMLElement)) {
            throw new Error('Modal body missing');
        }

        body.querySelector('.room-info__modal-wrapper')?.remove();
        body.append(wrapper);
        customElements.upgrade(body);
        this.updatedCallback();
        this.#promoteHeroImage();
        this.#initYotpo();

        await this.#decodeHeroImage();
        if (signal.aborted || !this.refs.dialog.open) return;

        this.#ready = true;
        this.#setBusy(false);
    }

    /**
     * @param {HTMLElement} root
     * @param {ModalConfig} config
     */
    #applyBannerSettings(root, config) {
        const title = root.querySelector('.room-info__modal-title');
        if (title instanceof HTMLElement && config.titleId) {
            title.id = config.titleId;
        }

        const wraps = root.querySelectorAll('.room-info__modal-price-wrap');
        wraps.forEach((wrap, index) => {
            if (!(wrap instanceof HTMLElement)) return;
            if (config.originalPriceCents != null) wrap.dataset.originalPrice = String(config.originalPriceCents);
            if (config.finalPriceCents != null) wrap.dataset.finalPrice = String(config.finalPriceCents);

            if (index === 0) {
                wrap.replaceChildren();
                if (config.isFree) {
                    wrap.append(
                        this.#priceEl(
                            'room-info__modal-price room-info__modal-price--compare',
                            config.originalPriceMoney
                        )
                    );
                } else if (config.hasDiscount) {
                    wrap.append(
                        this.#priceEl(
                            'room-info__modal-price room-info__modal-price--compare',
                            config.originalPriceMoney
                        )
                    );
                    wrap.append(
                        this.#priceEl('room-info__modal-price room-info__modal-price--sale', config.finalPriceMoney)
                    );
                } else {
                    wrap.append(this.#priceEl('room-info__modal-price', config.finalPriceMoney));
                }
                return;
            }

            wrap.replaceChildren();
            if (config.isFree && config.badgeText) {
                wrap.append(this.#priceEl('room-info__modal-price--free', config.badgeText));
            } else if (config.hasDiscount && config.badgeText) {
                wrap.append(this.#priceEl('room-info__modal-badge', config.badgeText));
            }
        });

        const yotpo = root.querySelector('.yotpo.bottomLine');
        if (yotpo instanceof HTMLElement && config.yotpoAppKey) {
            yotpo.setAttribute('data-appkey', config.yotpoAppKey);
        }

        const more = root.querySelector('.room-info__features-toggle-more');
        const less = root.querySelector('.room-info__features-toggle-less');
        if (more && config.featuresMore) more.textContent = config.featuresMore;
        if (less && config.featuresLess) less.textContent = config.featuresLess;
    }

    /**
     * @param {string} className
     * @param {string} [text]
     * @returns {HTMLSpanElement}
     */
    #priceEl(className, text = '') {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        return span;
    }

    #promoteHeroImage() {
        const image = this.querySelector('img.room-info__modal-image');
        if (!(image instanceof HTMLImageElement)) return;
        image.loading = 'eager';
        image.fetchPriority = 'high';
    }

    async #decodeHeroImage() {
        const image = this.querySelector('img.room-info__modal-image');
        if (!(image instanceof HTMLImageElement)) return;
        try {
            await image.decode();
        } catch (error) {
            console.error(error);
        }
    }

    #initYotpo() {
        if (window.yotpoWidgetsContainer?.initWidgets) {
            window.yotpoWidgetsContainer.initWidgets();
        } else if (window.yotpo?.refreshWidgets) {
            window.yotpo.refreshWidgets();
        }
    }

    #restoreFocus() {
        const trigger = this.#trigger;
        this.#trigger = null;
        if (trigger?.isConnected) trigger.focus();
    }

    /** @param {boolean} busy */
    #setBusy(busy) {
        const { dialog, body, loader } = this.refs;
        dialog.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (body instanceof HTMLElement) {
            body.setAttribute('aria-busy', busy ? 'true' : 'false');
        }
        if (loader instanceof HTMLElement) {
            loader.toggleAttribute('hidden', !busy);
        }
    }

    #bindSlider() {
        const slider = this.refs.slider;
        if (!(slider instanceof HTMLElement) || this.#sliderBound) return;

        this.#sliderAbort?.abort();
        this.#sliderAbort = new AbortController();
        const { signal } = this.#sliderAbort;
        this.#sliderBound = true;

        slider.addEventListener('pointerdown', this.#handlePointerDown, { signal });
        slider.addEventListener('click', this.#handleSliderClick, { signal });
        if ('onscrollend' in window) {
            slider.addEventListener('scrollend', this.#syncDots, { signal, passive: true });
        } else {
            slider.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
        }
    }

    #resetSlider() {
        const { slider } = this.refs;
        if (slider instanceof HTMLElement) slider.scrollLeft = 0;
        const dotsTrack = this.querySelector('.room-info__modal-dots-track');
        if (dotsTrack instanceof HTMLElement) dotsTrack.style.transform = '';
    }

    /** @param {Event} event */
    #handleDialogClick = (event) => {
        if (event.target === this.refs.dialog) this.closeModal();
    };

    /** @param {Event} event */
    #handleCancel = (event) => {
        event.preventDefault();
        this.closeModal();
    };

    /** @param {KeyboardEvent} event */
    #handleKeydown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.nextSlide();
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.prevSlide();
        }
    };

    #handleScroll = () => {
        clearTimeout(this.#scrollTimer);
        this.#scrollTimer = setTimeout(this.#syncDots, 150);
    };

    #syncDots = () => {
        const { slider, slides } = this.refs;
        if (!(slider instanceof HTMLElement) || !slides?.length) return;
        const index = Math.max(0, Math.min(Math.round(slider.scrollLeft / slider.offsetWidth), slides.length - 1));
        this.#currentSlide = index;
        const dots = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.room-info__modal-dot'));
        if (!dots.length) return;
        dots.forEach((dot, i) => dot.classList.toggle('room-info__modal-dot--active', i === index));
        this.#updateDotsWindow(index, dots.length);
    };

    /** @param {MouseEvent} event */
    #handleSliderClick = (event) => {
        if (!this.#ignoreClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.#ignoreClick = false;
    };

    /** @param {PointerEvent} event */
    #handlePointerDown = (event) => {
        const { slider, slides } = this.refs;
        if (!(slider instanceof HTMLElement) || !slides?.length || slides.length <= 1) return;
        if (event.button !== 0) return;
        if (event.pointerType !== 'mouse') return;

        event.preventDefault();
        slider.setPointerCapture(event.pointerId);

        const startX = event.clientX;
        const startScrollLeft = slider.scrollLeft;
        let moved = false;

        const controller = new AbortController();
        const { signal } = controller;

        slider.addEventListener(
            'pointermove',
            (/** @type {PointerEvent} */ e) => {
                const delta = startX - e.clientX;
                if (Math.abs(delta) > 3) moved = true;
                if (moved) slider.scrollLeft = startScrollLeft + delta;
            },
            { signal }
        );

        slider.addEventListener(
            'pointerup',
            (/** @type {PointerEvent} */ e) => {
                controller.abort();
                if (!moved) return;

                this.#ignoreClick = true;

                const totalDelta = startX - e.clientX;
                const threshold = slider.offsetWidth * 0.1;

                if (Math.abs(totalDelta) > threshold) {
                    if (totalDelta > 0) {
                        this.#goToSlideIndex(Math.min(this.#currentSlide + 1, slides.length - 1));
                    } else {
                        this.#goToSlideIndex(Math.max(this.#currentSlide - 1, 0));
                    }
                } else {
                    this.#goToSlideIndex(this.#currentSlide);
                }
            },
            { signal, once: true }
        );

        slider.addEventListener(
            'pointercancel',
            () => {
                controller.abort();
                if (!moved) return;
                const nearestIndex = Math.max(
                    0,
                    Math.min(Math.round(slider.scrollLeft / slider.offsetWidth), slides.length - 1)
                );
                this.#goToSlideIndex(nearestIndex);
            },
            { signal, once: true }
        );
    };

    /**
     * @param {number} index
     */
    #goToSlideIndex(index) {
        const { slider, slides } = this.refs;
        if (!(slider instanceof HTMLElement) || !slides?.length) return;

        this.#currentSlide = index;
        slider.scrollTo({
            left: index * slider.offsetWidth,
            behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        });

        const dots = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.room-info__modal-dot'));
        if (dots.length) {
            dots.forEach((dot, i) => dot.classList.toggle('room-info__modal-dot--active', i === index));
            this.#updateDotsWindow(index, dots.length);
        }
    }

    /**
     * Slides the dots track so the active dot stays within the 4-dot window.
     * @param {number} activeIndex
     * @param {number} totalDots
     */
    #updateDotsWindow(activeIndex, totalDots) {
        if (totalDots <= 4) return;
        const dotsTrack = this.querySelector('.room-info__modal-dots-track');
        if (!(dotsTrack instanceof HTMLElement)) return;
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const windowStart = Math.max(0, Math.min(activeIndex - 1, totalDots - 4));
        dotsTrack.style.transform = `translateX(${-(windowStart * rem)}px)`;
    }
}

if (!customElements.get('na-room-banner-modal-component')) {
    customElements.define('na-room-banner-modal-component', RoomBannerModalComponent);
}
